import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from 'react';
export const TimePicker = ({ value, onChange, className = '', id, 'data-testid': dataTestId, maxDurationHours = 1, operatingHours = null, disabled = false, variant = 'default', }) => {
    const [isOpen, setIsOpen] = useState(false);
    // Use operating hours from props, fallback to hardcoded 10:00 if not provided
    const defaultStartTime = operatingHours
        ? `${Math.floor(operatingHours.openMinutes / 60).toString().padStart(2, '0')}:${(operatingHours.openMinutes % 60).toString().padStart(2, '0')}`
        : '10:00';
    const [tempTime, setTempTime] = useState(value || defaultStartTime);
    const pickerRef = useRef(null);
    useEffect(() => {
        if (value) {
            setTempTime(value);
        }
    }, [value]);
    // Close picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);
    const handleApply = () => {
        onChange(tempTime);
        setIsOpen(false);
    };
    const handleCancel = () => {
        setTempTime(value || defaultStartTime);
        setIsOpen(false);
    };
    const formatDisplayTime = (time) => {
        if (!time)
            return '--:--';
        const [h, m] = time.split(':');
        const hour = parseInt(h, 10);
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${m} ${period}`;
    };
    // Generate hour options dynamically based on operating hours
    // Default to 10-24 (10AM-12AM) if operating hours not provided
    const startHour = operatingHours ? Math.floor(operatingHours.openMinutes / 60) : 10;
    const endHour = operatingHours ? Math.floor(operatingHours.closeMinutes / 60) : 24;
    const hourOptions = Array.from({ length: endHour - startHour + 1 }, (_, i) => {
        const hour = i + startHour;
        const hourValue = hour === 24 ? '00' : hour.toString().padStart(2, '0');
        const displayHour = hour === 24 ? 12 : hour > 12 ? hour - 12 : hour;
        const period = hour === 24 || hour < 12 ? 'AM' : 'PM';
        return { value: hourValue, label: `${displayHour} ${period}` };
    }).filter((opt) => {
        // Only show hours where startTime + duration <= closing time
        const hour = opt.value === '00' ? 24 : parseInt(opt.value, 10);
        const hourInMinutes = hour * 60;
        const endMinutes = hourInMinutes + maxDurationHours * 60;
        const closeMinutes = operatingHours ? operatingHours.closeMinutes : 24 * 60;
        return endMinutes <= closeMinutes;
    });
    // Generate minute options (0-59)
    const minuteOptions = Array.from({ length: 60 }, (_, i) => {
        const minute = i.toString().padStart(2, '0');
        return { value: minute, label: minute };
    });
    const isMissionControl = variant === 'mission-control';
    const triggerClass = isMissionControl
        ? `mc-input flex h-9 w-full cursor-pointer items-center justify-between text-sm disabled:cursor-not-allowed disabled:opacity-50 ${className}`
        : `flex h-9 w-full rounded-md border border-slate-600 bg-slate-900/50 px-3 py-1 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 cursor-pointer items-center justify-between disabled:cursor-not-allowed disabled:opacity-50 ${className}`;
    const popoverClass = isMissionControl
        ? 'mc-popover-content absolute right-0 z-50 mt-1 w-64 max-w-[calc(100vw-2rem)] rounded p-4'
        : 'absolute z-50 mt-1 w-64 bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-4';
    const labelClass = isMissionControl ? 'mc-field-label mb-1 block' : 'block text-xs text-slate-400 mb-1';
    const selectClass = isMissionControl
        ? 'mc-input w-full min-w-0 px-2 py-1.5 text-sm'
        : 'w-full bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50';
    const cancelClass = isMissionControl
        ? 'mc-action-btn mc-action-btn-compact mc-action-btn-fit flex-1'
        : 'flex-1 rounded-md border border-slate-600 px-3 py-2 text-sm text-white hover:bg-slate-700';
    const applyClass = isMissionControl
        ? 'mc-action-btn mc-action-btn-primary mc-action-btn-compact mc-action-btn-fit flex-1'
        : 'flex-1 rounded-md bg-amber-500 px-3 py-2 text-sm font-medium text-black hover:bg-amber-600';
    return (_jsxs("div", { className: "relative", ref: pickerRef, style: { width: '100%' }, children: [_jsxs("button", { type: "button", id: id, "data-testid": dataTestId, disabled: disabled, onClick: () => !disabled && setIsOpen(!isOpen), className: triggerClass, style: { width: '100%' }, children: [_jsx("span", { children: formatDisplayTime(value) }), _jsx("svg", { className: "w-4 h-4 text-slate-400", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" }) })] }), isOpen && (_jsxs("div", { "data-testid": "time-picker-popover", className: popoverClass, children: [_jsxs("div", { className: "flex gap-3 mb-4", children: [_jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("label", { className: labelClass, children: "Hour" }), _jsx("select", { value: tempTime.split(':')[0], disabled: disabled, onChange: (e) => {
                                            const [, m] = tempTime.split(':');
                                            setTempTime(`${e.target.value}:${m}`);
                                        }, className: selectClass, children: hourOptions.map((opt) => (_jsx("option", { value: opt.value, children: opt.label }, opt.value))) })] }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("label", { className: labelClass, children: "Minute" }), _jsx("select", { value: tempTime.split(':')[1], disabled: disabled, onChange: (e) => {
                                            const [h] = tempTime.split(':');
                                            setTempTime(`${h}:${e.target.value}`);
                                        }, className: selectClass, children: minuteOptions.map((opt) => (_jsx("option", { value: opt.value, children: opt.label }, opt.value))) })] })] }), _jsx("div", { className: isMissionControl ? 'text-center text-[color:var(--mc-text-primary)] text-lg font-semibold mb-4' : 'text-center text-slate-300 text-lg font-semibold mb-4', children: formatDisplayTime(tempTime) }), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { type: "button", onClick: handleCancel, className: cancelClass, children: "Cancel" }), _jsx("button", { type: "button", onClick: handleApply, className: applyClass, children: "Apply" })] })] }))] }));
};
